/**
 * Modern Bold · <mb-search-portal> custom element
 * ─────────────────────────────────────────────────────────────────────────
 * The C·Center Portal search modal. Owns:
 *   • Open / close from any [data-mb-search-trigger], with focus trap,
 *     body scroll lock, ESC, click-outside, and focus restoration.
 *   • Recently viewed rendering (both desktop grid AND mobile rail) from
 *     the localStorage-backed window.MBRecentlyViewed store.
 *   • Live predictive suggestions: typing ≥ MIN_CHARS triggers a
 *     debounced fetch to Shopify's Section Rendering API
 *     (/search/suggest?…&section_id=modern-predictive-search). The
 *     response HTML is extracted from [data-mb-search-results-payload]
 *     and swapped into [data-mb-search-results].
 *   • Single data-state attribute on the host drives all visibility:
 *       lobby | loading | results | empty
 *     CSS reads the attribute; JS just flips it.
 *
 * Public API (window.MBRecentlyViewed):
 *   .add(product)  — called by <mb-recently-viewed-tracker> on every PDP load.
 *   .get()         — returns the stored array (max 8, newest first).
 *   .clear()       — empties storage.
 * Defined at module top so the PDP tracker can call .add() before the
 * portal element ever connects.
 */

const RECENT_KEY  = 'mb_recently_viewed';
const MAX_RECENT  = 8;
const DEBOUNCE_MS = 250;
const MIN_CHARS   = 2;
const SECTION_ID  = 'modern-predictive-search';

const getRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') ?? []; }
  catch { return []; }
};
const saveRecent = (list) => {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch { /* quota / private mode */ }
};

window.MBRecentlyViewed ??= {
  add: (product) => {
    if (!product?.id) return;
    const list = getRecent().filter((item) => item.id !== product.id);
    list.unshift(product);
    saveRecent(list.slice(0, MAX_RECENT));
  },
  get: getRecent,
  clear: () => saveRecent([]),
};

const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

/**
 * One recently-viewed card. Class names match modern-search.css; the
 * markup is shared between the desktop grid and the mobile rail (CSS
 * decides which surface is visible at each breakpoint).
 */
const renderRecentCard = (p) => {
  const url   = escapeHtml(p.url   ?? '#');
  const title = escapeHtml(p.title ?? '');
  const img   = escapeHtml(p.image ?? '');
  const price = escapeHtml(p.price == null ? '' : String(p.price));
  return `
    <a class="mb-search-portal__recent-card" href="${url}">
      <div class="mb-search-portal__recent-card-frame">
        ${img ? `<img class="mb-search-portal__recent-card-img" src="${img}" alt="" loading="lazy">` : ''}
      </div>
      <div class="mb-search-portal__recent-card-title">${title}</div>
      ${price ? `<div class="mb-search-portal__recent-card-price">${price}</div>` : ''}
    </a>
  `;
};

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

if (!customElements.get('mb-search-portal')) {
  class MBSearchPortal extends HTMLElement {
    #cleanups   = [];
    #abortCtrl  = null;
    #debounceId = 0;
    #lastTrigger = null;
    #refs = {};

    connectedCallback() {
      this.#cacheRefs();
      this.#bindEvents();
    }

    disconnectedCallback() {
      this.#cleanups.forEach((fn) => fn());
      this.#cleanups = [];
      this.#abortCtrl?.abort();
      clearTimeout(this.#debounceId);
    }

    // ── DOM cache ──────────────────────────────────────────────────────────

    #cacheRefs() {
      this.#refs = {
        input:         this.querySelector('[data-mb-search-input]'),
        results:       this.querySelector('[data-mb-search-results]'),
        loading:       this.querySelector('[data-mb-search-loading]'),
        empty:         this.querySelector('[data-mb-search-empty]'),
        recentDesktop: this.querySelector('[data-mb-search-recent]'),
        recentGrid:    this.querySelector('[data-mb-search-recent-grid]'),
        recentMobile:  this.querySelector('[data-mb-search-recent-mobile]'),
        recentRail:    this.querySelector('[data-mb-search-recent-rail]'),
      };
    }

    // ── Event wiring ───────────────────────────────────────────────────────

    #bindEvents() {
      // Triggers — delegated on document so the listener survives header
      // section reloads (the trigger button gets a new DOM node, but the
      // selector still hits it). Same listener handles all triggers.
      const onDocClick = (e) => {
        const trigger = e.target.closest('[data-mb-search-trigger]');
        if (!trigger) return;
        e.preventDefault();
        this.open(trigger);
      };
      document.addEventListener('click', onDocClick);
      this.#cleanups.push(() => document.removeEventListener('click', onDocClick));

      // ESC closes from anywhere while the modal is open.
      const onDocKeydown = (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      };
      document.addEventListener('keydown', onDocKeydown);
      this.#cleanups.push(() => document.removeEventListener('keydown', onDocKeydown));

      // Single click delegate covers: close buttons, clear button, result
      // tabs, query suggestions. Saves four separate listeners and keeps
      // the swapped-in server HTML automatically wired without re-binding.
      const onModalClick = (e) => {
        if (e.target.closest('[data-mb-search-close]')) {
          this.close();
          return;
        }
        if (e.target.closest('[data-mb-search-clear]')) {
          this.#clearRecent();
          return;
        }
        const tab = e.target.closest('[data-mb-search-tab]');
        if (tab) {
          e.preventDefault();
          this.#switchTab(tab.dataset.mbSearchTab);
          return;
        }
        const queryBtn = e.target.closest('[data-query]');
        if (queryBtn) {
          e.preventDefault();
          const q = queryBtn.dataset.query ?? '';
          this.#refs.input.value = q;
          this.#refs.input.focus();
          this.#runSearch(q);
        }
      };
      this.addEventListener('click', onModalClick);
      this.#cleanups.push(() => this.removeEventListener('click', onModalClick));

      // Input — debounced live search.
      const onInput = (e) => {
        if (e.target !== this.#refs.input) return;
        this.#runSearch(e.target.value);
      };
      this.addEventListener('input', onInput);
      this.#cleanups.push(() => this.removeEventListener('input', onInput));

      // Focus trap — Tab / Shift-Tab cycle within the modal while open.
      const onTrap = (e) => this.#trapFocus(e);
      this.addEventListener('keydown', onTrap);
      this.#cleanups.push(() => this.removeEventListener('keydown', onTrap));
    }

    // ── State machine ──────────────────────────────────────────────────────

    /** lobby | loading | results | empty */
    #setState(name) {
      this.dataset.state = name;
    }

    // ── Recently viewed ────────────────────────────────────────────────────

    /**
     * Renders the localStorage list into both surfaces simultaneously
     * (desktop grid + mobile rail). CSS picks which one is visible at
     * the current breakpoint. Wrappers stay hidden when the list is empty
     * so a first-time visitor doesn't see a ghost heading.
     */
    #renderRecent() {
      const list = getRecent();
      const { recentDesktop, recentMobile, recentGrid, recentRail } = this.#refs;

      if (!list.length) {
        if (recentDesktop) recentDesktop.hidden = true;
        if (recentMobile)  recentMobile.hidden  = true;
        return;
      }

      const html = list.map(renderRecentCard).join('');
      if (recentGrid)    recentGrid.innerHTML  = html;
      if (recentRail)    recentRail.innerHTML  = html;
      if (recentDesktop) recentDesktop.hidden  = false;
      if (recentMobile)  recentMobile.hidden   = false;
    }

    #clearRecent() {
      window.MBRecentlyViewed.clear();
      const { recentDesktop, recentMobile, recentGrid, recentRail } = this.#refs;
      if (recentGrid)    recentGrid.innerHTML  = '';
      if (recentRail)    recentRail.innerHTML  = '';
      if (recentDesktop) recentDesktop.hidden  = true;
      if (recentMobile)  recentMobile.hidden   = true;
    }

    // ── Live search ────────────────────────────────────────────────────────

    /**
     * Routes a query string through the debounce path. Empty / short
     * queries restore the lobby view; long enough queries kick off a
     * fetch after DEBOUNCE_MS of typing pause.
     */
    #runSearch(raw) {
      const q = (raw ?? '').trim();
      clearTimeout(this.#debounceId);
      if (q.length < MIN_CHARS) {
        this.#abortCtrl?.abort();
        this.#setState('lobby');
        return;
      }
      this.#debounceId = window.setTimeout(() => this.#fetchResults(q), DEBOUNCE_MS);
    }

    /**
     * Section Rendering API call to /search/suggest. Returns server-
     * rendered HTML; we extract our payload div and inject it.
     * AbortController cancels any in-flight request from a faster typist
     * so the latest keystroke always wins.
     */
    async #fetchResults(q) {
      this.#abortCtrl?.abort();
      this.#abortCtrl = new AbortController();
      this.#setState('loading');

      const url =
        `/search/suggest?q=${encodeURIComponent(q)}` +
        `&section_id=${SECTION_ID}` +
        '&resources[type]=product,collection,page,query' +
        '&resources[limit]=10' +
        '&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=last';

      try {
        const res = await fetch(url, { signal: this.#abortCtrl.signal });
        const html = await res.text();
        const tmpl = document.createElement('div');
        tmpl.innerHTML = html;
        const payload = tmpl.querySelector('[data-mb-search-results-payload]');

        if (!payload) {
          this.#setState('empty');
          return;
        }

        const hasTabs = !!payload.querySelector('[data-mb-search-tab]');
        const hasQueriesOnly =
          payload.classList.contains('mb-search-modal__results--queries-only') ||
          !!payload.querySelector('.mb-search-modal__results--queries-only');

        this.#refs.results.innerHTML = '';
        this.#refs.results.appendChild(payload);

        if (!hasTabs && !hasQueriesOnly) {
          this.#setState('empty');
          return;
        }

        const firstTab = this.#refs.results.querySelector('[data-mb-search-tab]');
        if (firstTab) this.#switchTab(firstTab.dataset.mbSearchTab);
        this.#setState('results');
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.#setState('empty');
      }
    }

    /**
     * Tab pill activation. The tab/panel markup lives inside the section
     * payload, so we re-query on every call instead of caching refs that
     * would break on the next swap.
     */
    #switchTab(name) {
      const tabs   = this.#refs.results.querySelectorAll('[data-mb-search-tab]');
      const panels = this.#refs.results.querySelectorAll('[data-mb-search-panel]');
      tabs.forEach((t) => {
        const active = t.dataset.mbSearchTab === name;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      panels.forEach((p) => {
        p.hidden = p.dataset.mbSearchPanel !== name;
      });
    }

    // ── Focus trap ─────────────────────────────────────────────────────────

    #trapFocus(e) {
      if (e.key !== 'Tab') return;
      const focusable = [...this.querySelectorAll(FOCUSABLE)].filter(
        (el) => !el.hasAttribute('hidden') && !el.closest('[hidden]')
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last  = focusable.at(-1);
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // ── Open / close (public API) ──────────────────────────────────────────

    open(trigger = null) {
      this.#lastTrigger = trigger ?? document.activeElement;
      this.#refs.input.value = '';
      this.#setState('lobby');
      this.#renderRecent();

      this.hidden = false;
      // Reflow so the .is-open transition runs from the hidden→visible boundary.
      // eslint-disable-next-line no-unused-expressions
      this.offsetHeight;
      this.classList.add('is-open');
      document.body.classList.add('mb-search-open');

      requestAnimationFrame(() => this.#refs.input.focus());
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('mb-search-open');
      this.#abortCtrl?.abort();
      clearTimeout(this.#debounceId);

      // Wait for the CSS transition (~200ms + buffer) before removing
      // the element from layout entirely.
      setTimeout(() => { this.hidden = true; }, 220);

      if (typeof this.#lastTrigger?.focus === 'function') {
        this.#lastTrigger.focus();
      }
    }
  }

  customElements.define('mb-search-portal', MBSearchPortal);
}
